'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { LuArrowLeft, LuHouse } from 'react-icons/lu';

export default function NotFound() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div className='relative min-h-screen bg-gray-900 flex items-center justify-center'>
      {/* Background Overlay */}
      <div className='absolute inset-0 bg-linear-to-b from-gray-900/95 via-gray-900/50 to-gray-900' />

      {/* Animated Background Elements */}
      <div className='absolute inset-0 overflow-hidden'>
        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMTIxMjEiIGZpbGwtb3BhY2l0eT0iMC40Ij48cGF0aCBkPSJNMzYgMzBoMmwyLTJWMGgtMnYzMHoiLz48cGF0aCBkPSJNMzAgMGgydjJIMzB6TTAgMGgydjJIMHoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-5" />

        {/* Floating Gradient Orbs */}
        <div className='absolute top-1/4 right-1/4 h-72 w-72 rounded-full bg-red-500/20 blur-3xl animate-float' />
        <div className='absolute bottom-1/4 left-1/4 h-64 w-64 rounded-full bg-blue-500/20 blur-3xl animate-float-delayed' />
        <div className='absolute top-1/2 left-1/2 h-56 w-56 rounded-full bg-purple-500/20 blur-3xl animate-float-slow' />
      </div>

      {/* Content Container */}
      <div className='relative z-10 w-full max-w-3xl mx-auto px-4 sm:px-6 text-center'>
        {/* 404 Number with Glitch Effect */}
        <div className='relative mb-8'>
          <h1 className='text-9xl font-extrabold text-white opacity-20'>404</h1>
          <div className='absolute inset-0 flex items-center justify-center'>
            <h1 className='text-9xl font-extrabold text-transparent bg-clip-text bg-linear-to-r from-blue-400 via-purple-400 to-red-400'>
              404
            </h1>
          </div>
        </div>

        {/* Message */}
        <h2 className='text-3xl sm:text-4xl font-bold text-white mb-4'>
          Page Not Found
        </h2>
        <p className='text-xl text-gray-300 mb-12 max-w-xl mx-auto'>
          The page you are looking for doesn't exist or has been moved. Let's
          get you back on track.
        </p>

        {/* CTA Buttons */}
        <div className='flex flex-col sm:flex-row items-center justify-center gap-4'>
          <Link
            href='/'
            className='group relative px-8 py-4 rounded-full bg-white text-gray-900 font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-white/20 flex items-center'
          >
            <LuHouse className='mr-2' />
            Back to Home
            <div className='absolute inset-0 rounded-full bg-white/40 animate-pulse-slow -z-10' />
          </Link>
          <button
            onClick={() => window.history.back()}
            className='group px-8 py-4 rounded-full bg-linear-to-r from-blue-500/10 to-purple-500/10 text-white font-semibold backdrop-blur-xs transition-all duration-300 hover:from-blue-500/20 hover:to-purple-500/20 flex items-center'
          >
            <LuArrowLeft className='mr-2' />
            Go Back
          </button>
        </div>
      </div>

      {/* Animation Styles */}
      <style jsx>{`
        @keyframes float {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-20px);
          }
        }

        .animate-float {
          animation: float 6s ease-in-out infinite;
        }

        .animate-float-delayed {
          animation: float 6s ease-in-out 2s infinite;
        }

        .animate-float-slow {
          animation: float 8s ease-in-out 1s infinite;
        }

        .animate-pulse-slow {
          animation: pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </div>
  );
}
